import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { usePWA } from '@/hooks/usePWA';
import { pwaService } from '@/services/PWAService';
import { Download, Check, Trash2, RefreshCw, Wifi, WifiOff, AlertCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

export const PWASettings: React.FC = () => {
  const { isInstallable, isStandalone, isOnline, connectionStatus, installApp } = usePWA();
  const navigate = useNavigate();
  const [cacheSize, setCacheSize] = useState<number>(0);
  const [isClearing, setIsClearing] = useState(false);

  useEffect(() => {
    loadCacheSize();
  }, []);

  const loadCacheSize = async () => {
    const size = await pwaService.getCacheSize();
    setCacheSize(size);
  };

  const handleInstall = async () => {
    if (isInstallable) {
      await installApp();
      toast.success('App installed successfully!');
    } else {
      navigate('/install');
    }
  };

  const handleClearCache = async () => {
    setIsClearing(true);
    try {
      await pwaService.clearCache();
      await loadCacheSize();
      toast.success('Cache cleared successfully');
    } catch (error) {
      toast.error('Failed to clear cache');
      console.error('Cache clear error:', error);
    } finally {
      setIsClearing(false);
    }
  };

  const formatBytes = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Progressive Web App</CardTitle>
        <CardDescription>
          Install the app for offline access and better performance
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Installation Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">Installation Status</span>
              {isStandalone ? (
                <Badge variant="default" className="bg-green-600">
                  <Check className="h-3 w-3 mr-1" />
                  Installed
                </Badge>
              ) : (
                <Badge variant="secondary">Not Installed</Badge>
              )}
            </div>
          </div>

          {!isStandalone && (
            <Button onClick={handleInstall} className="w-full sm:w-auto">
              <Download className="h-4 w-4 mr-2" />
              {isInstallable ? 'Install Now' : 'View Installation Guide'}
            </Button>
          )}
        </div>

        {/* Connection Status */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="font-medium">Connection Status</span>
              <Badge 
                variant={isOnline ? 'default' : 'destructive'}
                className={isOnline ? 'bg-green-600' : ''}
              >
                {isOnline ? (
                  <>
                    <Wifi className="h-3 w-3 mr-1" />
                    Online
                  </>
                ) : (
                  <>
                    <WifiOff className="h-3 w-3 mr-1" />
                    Offline
                  </>
                )}
              </Badge>
            </div>
          </div>
          
          {connectionStatus === 'uncertain' && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 text-sm">
              <AlertCircle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
              <span className="text-yellow-800 dark:text-yellow-200">
                Connection is unstable. Some features may be limited.
              </span>
            </div>
          )}
        </div>

        {/* Cache Management */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <span className="font-medium">Cached Data</span>
            <span className="text-sm text-muted-foreground">{formatBytes(cacheSize)}</span>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={loadCacheSize}
              disabled={isClearing}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleClearCache}
              disabled={isClearing}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {isClearing ? 'Clearing...' : 'Clear Cache'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Cached data allows the app to work offline and load faster. Clearing cache will require re-downloading content.
          </p>
        </div>

        {/* Service Worker Status */}
        <div className="space-y-2">
          <span className="font-medium">Service Worker</span>
          <div className="text-sm text-muted-foreground">
            {pwaService.getRegistration() ? (
              <div className="flex items-center gap-2 text-green-600 dark:text-green-500">
                <Check className="h-4 w-4" />
                Active and running
              </div>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground">
                <AlertCircle className="h-4 w-4" />
                Not active
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
